// Skysight's smaller cumulus-stack cloud icon (different from the row icon).
// Decoded from docs/skysight-skewt-bundle.js cu-stack-el rendering.
// Natural extent approximately: x ∈ [0, 88], y ∈ [c=20, m=49] (height 29).
const STACK_ICON_PATH =
    'M54.775 34.033c-1.541-8.788-8.94-15.479-17.944-15.479-8.131 0-15.02 5.444-17.362 12.955' +
    '-.672-.123-1.365-.19-2.075-.19-5.603 0-10.147 4.05-10.147 9.049s4.544 9.049 10.147 9.049' +
    'c.457 0 39.606 0 39.957 0 4.416 0 7.997-3.195 7.997-7.137 0-3.94-3.58-7.135-7.994-7.137' +
    '-.871 0-1.708.125-2.495.358l-.084-.468z';

const ICON_NATURAL_W = 88;       // Skysight's `w`
const ICON_NATURAL_Y_TOP = 20;   // Skysight's `c`
const ICON_NATURAL_Y_BOT = 49;   // Skysight's `m`
const ICON_NATURAL_H = ICON_NATURAL_Y_BOT - ICON_NATURAL_Y_TOP; // 29

/**
 * Render Skysight's "cu-stack-el" — a vertical column of cloud icons that
 * visualizes the cumulus tower from the cloud BASE up to the Equilibrium
 * Level. This is what's drawn on convective days regardless of `cldfra`
 * (which is often zero in NWP output for cumulus-favorable conditions).
 *
 * Source: docs/skysight-skewt-bundle.js cu-stack-el block:
 *   u = clamp(2, 10, ceil(l/40));   // number of layers
 *   e = l / u;                       // pixel step per layer
 *   for h in [u-1 .. 0]:
 *     o      = (h === 0) ? d : d - h*e   // y for this layer (d=base y)
 *     scaleX = 0.6 * (1 - h/u * 0.6)     // shrink toward top (perspective)
 *     M      = e*1.5 / icon_height       // vertical scale to match step
 *     translate(i - b/2, o - m*M - 20) scale(scaleX, M) → path
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array} data
 * @param {number} plcl - LCL pressure (cumulus base) in hPa
 * @param {{pressure: number, temperature?: number}} el - Equilibrium Level
 */
export function drawCumulusStackEL(container, scales, data, plcl, el) {
    container.selectAll('.skewt-ext-cumulus-stack-el').remove();
    if (!data || data.length === 0) return;
    if (!Number.isFinite(plcl) || !el || !Number.isFinite(el.pressure)) return;

    const surface = data[0];
    const oktasRaw = surface.cu_oktas;
    if (!Number.isFinite(oktasRaw) || oktasRaw < 1) return;

    // Bottom of stack = LCL pressure (cumulus cloudbase). The row of cumulus
    // icons drawn by render-cumulus.js spans LCL → PBL top; anchoring the
    // stack at LCL means its lowermost cloud sits AT the row's bottom, so
    // visually the stack and the row connect seamlessly (no gap between
    // "Cu Cloudbase" and the convective tower).
    const pBase = plcl;
    const yBase = scales.y(pBase);        // bottom of stack (larger y)
    const yTop = scales.y(el.pressure);   // top of stack (smaller y, near EL)
    const l = yBase - yTop;               // total stack height in px
    if (l < 30) return;                   // Skysight skips paper-thin stacks

    const u = Math.max(2, Math.min(10, Math.ceil(l / 40)));   // layer count
    const e = l / u;                       // px per layer
    const xCenter = scales.x(scales.tMin) + 40;  // column anchor x

    const layer = container.append('g').attr('class', 'skewt-ext-cumulus-stack-el');

    for (let h = u - 1; h >= 0; h--) {
        // o = y position of this layer (h=0 is bottom, h=u-1 is top near EL).
        const o = h === 0 ? yBase : yBase - h * e;
        // Shrink horizontally for perspective: 0.6 at h=0, smaller toward top.
        const scaleX = 0.6 * (1 - (h / u) * 0.6);
        const iconW = ICON_NATURAL_W * scaleX;
        const scaleY = (e * 1.5) / ICON_NATURAL_H;
        const x = xCenter - iconW / 2;

        const iconG = layer
            .append('g')
            .attr('class', `cu-stack-layer-${h}`)
            .attr(
                'transform',
                `translate(${x}, ${o - ICON_NATURAL_Y_BOT * scaleY - 20})`,
            );
        iconG
            .append('g')
            .attr('transform', `scale(${scaleX}, ${scaleY})`)
            .append('path')
            .attr('d', STACK_ICON_PATH)
            .style('fill', 'white')
            .style('stroke', 'rgba(128, 128, 128, 0.8)')
            .style('stroke-width', 2)
            .style('vector-effect', 'non-scaling-stroke');
    }
}
